import { useState, useEffect, useRef } from 'react';
import type { ArticleDraft } from '../../../types/article';
import { slugify } from '../../../utils/slugify';

interface RichEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const TOOLBAR_ACTIONS = [
  { icon: 'ri-bold', command: 'bold', label: 'Bold' },
  { icon: 'ri-italic', command: 'italic', label: 'Italic' },
  { icon: 'ri-underline', command: 'underline', label: 'Underline' },
];

const HEADING_TAGS: { label: string; tag: string }[] = [
  { label: 'Paragraph', tag: 'p' },
  { label: 'Heading 2', tag: 'h2' },
  { label: 'Heading 3', tag: 'h3' },
];

export default function RichEditor({ value, onChange }: RichEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isUpdating = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isUpdating.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
  }, [value]);

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      isUpdating.current = true;
      onChange(editorRef.current.innerHTML);
      setTimeout(() => { isUpdating.current = false; }, 0);
    }
  };

  const insertHeading = (tag: string) => {
    document.execCommand('formatBlock', false, tag);
    editorRef.current?.focus();
    handleInput();
  };

  const insertBulletList = () => {
    document.execCommand('insertUnorderedList', false);
    editorRef.current?.focus();
    handleInput();
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  return (
    <div className="border border-[#2a2a2a] rounded-md overflow-hidden bg-[#0a0a0a]">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-2 bg-[#111] border-b border-[#1f1f1f] flex-wrap">
        {TOOLBAR_ACTIONS.map((action) => (
          <button
            key={action.command}
            type="button"
            title={action.label}
            onMouseDown={(e) => { e.preventDefault(); exec(action.command); }}
            className="w-8 h-8 flex items-center justify-center rounded text-gray-400 hover:bg-[#222] hover:text-white transition-colors cursor-pointer"
          >
            <i className={action.icon} />
          </button>
        ))}
        <div className="w-px h-5 bg-[#2a2a2a] mx-1" />
        {HEADING_TAGS.map((h) => (
          <button
            key={h.tag}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); insertHeading(h.tag); }}
            className="text-xs text-gray-400 hover:text-white hover:bg-[#222] px-2 py-1 rounded transition-colors cursor-pointer whitespace-nowrap font-['Barlow_Condensed'] font-bold"
          >
            {h.label}
          </button>
        ))}
        <div className="w-px h-5 bg-[#2a2a2a] mx-1" />
        <button
          type="button"
          title="Bullet List"
          onMouseDown={(e) => { e.preventDefault(); insertBulletList(); }}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-400 hover:bg-[#222] hover:text-white transition-colors cursor-pointer"
        >
          <i className="ri-list-unordered" />
        </button>
        <button
          type="button"
          title="Insert Link"
          onMouseDown={(e) => { e.preventDefault(); insertLink(); }}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-400 hover:bg-[#222] hover:text-white transition-colors cursor-pointer"
        >
          <i className="ri-link" />
        </button>
      </div>

      {/* Editable content area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        className="min-h-[320px] p-5 text-gray-300 text-sm leading-relaxed focus:outline-none prose-invert"
        style={{
          lineHeight: '1.75',
          fontFamily: 'inherit',
        }}
        data-placeholder="Write your full article content here..."
      />

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #444;
          pointer-events: none;
        }
        [contenteditable] h2 { font-size: 1.4rem; font-weight: 700; color: #fff; margin: 1rem 0 0.5rem; }
        [contenteditable] h3 { font-size: 1.1rem; font-weight: 600; color: #e5e5e5; margin: 0.75rem 0 0.4rem; }
        [contenteditable] p { margin: 0 0 0.75rem; }
        [contenteditable] ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        [contenteditable] a { color: #00ff41; text-decoration: underline; }
        [contenteditable] strong { color: #fff; }
      `}</style>
    </div>
  );
}

// ─── Article Form ──────────────────────────────────────────────────────────────

const CATEGORIES = ['Warzone', 'Multiplayer', 'Zombies', 'Esports', 'Community', 'Leaks'] as const;

interface ArticleFormProps {
  initial?: Partial<ArticleDraft>;
  onSubmit: (data: ArticleDraft, publish: boolean) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

export function ArticleForm({ initial, onSubmit, onCancel, submitting }: ArticleFormProps) {
  const [form, setForm] = useState<ArticleDraft>({
    title: initial?.title ?? '',
    slug: initial?.slug ?? '',
    excerpt: initial?.excerpt ?? '',
    content: initial?.content ?? '',
    category: initial?.category ?? 'Warzone',
    author: initial?.author ?? '',
    date: initial?.date ?? new Date().toISOString(),
    image: initial?.image ?? '',
    read_time: initial?.read_time ?? 3,
    featured: initial?.featured ?? false,
    published: initial?.published ?? false,
  });

  const set = (key: keyof ArticleDraft, val: unknown) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const handleTitleChange = (value: string) => {
    set('title', value);
    // Auto-generate slug only if slug hasn't been manually edited
    if (!form.slug || form.slug === slugify(form.title)) {
      set('slug', slugify(value));
    }
  };

  const handleSubmit = async (publish: boolean) => {
    await onSubmit({ ...form, published: publish }, publish);
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); handleSubmit(form.published); }}
      className="space-y-6"
    >
      {/* Title */}
      <div>
        <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
          Article Title *
        </label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          required
          placeholder="Enter a bold, punchy headline..."
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-[#00ff41]/50 placeholder-gray-600 transition-colors"
        />
        {form.slug && (
          <p className="text-[#444] text-xs mt-1.5 font-mono">
            URL: /news/<span className="text-[#00ff41]/70">{form.slug}</span>
          </p>
        )}
      </div>

      {/* Slug (editable) */}
      <div>
        <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
          URL Slug
        </label>
        <input
          type="text"
          value={form.slug}
          onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))}
          placeholder="auto-generated-from-title"
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-[#aaa] text-sm rounded-md px-4 py-3 focus:outline-none focus:border-[#00ff41]/50 placeholder-gray-600 transition-colors font-mono"
        />
        <p className="text-[#333] text-xs mt-1">Edit to customise the article URL. Auto-filled from the title.</p>
      </div>

      {/* Excerpt */}
      <div>
        <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
          Excerpt / Teaser
        </label>
        <textarea
          value={form.excerpt}
          onChange={(e) => set('excerpt', e.target.value)}
          rows={3}
          placeholder="Short description shown in the news feed preview..."
          maxLength={500}
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-[#00ff41]/50 placeholder-gray-600 transition-colors resize-none"
        />
        <p className="text-gray-600 text-xs mt-1 text-right">{form.excerpt.length}/500</p>
      </div>

      {/* Row: Category + Author + Read time */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
            Category *
          </label>
          <select
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-[#00ff41]/50 cursor-pointer"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
            Author *
          </label>
          <input
            type="text"
            value={form.author}
            onChange={(e) => set('author', e.target.value)}
            required
            placeholder="e.g. Ghost_Actual"
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-[#00ff41]/50 placeholder-gray-600 transition-colors"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
            Read Time (min)
          </label>
          <input
            type="number"
            min={1}
            max={60}
            value={form.read_time}
            onChange={(e) => set('read_time', Number(e.target.value))}
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-[#00ff41]/50 transition-colors"
          />
        </div>
      </div>

      {/* Cover Image URL */}
      <div>
        <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
          Cover Image URL
        </label>
        <input
          type="url"
          value={form.image}
          onChange={(e) => set('image', e.target.value)}
          placeholder="https://..."
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-4 py-3 focus:outline-none focus:border-[#00ff41]/50 placeholder-gray-600 transition-colors"
        />
        {form.image && (
          <div className="mt-3 rounded-md overflow-hidden h-32 w-full">
            <img src={form.image} alt="preview" className="w-full h-full object-cover object-top" />
          </div>
        )}
      </div>

      {/* Content */}
      <div>
        <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
          Article Content *
        </label>
        <RichEditor value={form.content} onChange={(v) => set('content', v)} />
      </div>

      {/* Toggles */}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-3 cursor-pointer group">
          <div
            onClick={() => set('featured', !form.featured)}
            className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${form.featured ? 'bg-[#ff6b35]' : 'bg-[#2a2a2a]'}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${form.featured ? 'left-6' : 'left-1'}`} />
          </div>
          <span className="text-gray-400 text-sm group-hover:text-white transition-colors">Featured Article</span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-[#1f1f1f]">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          ← Back
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            disabled={submitting}
            onClick={() => handleSubmit(false)}
            className="border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#444] text-sm font-['Barlow_Condensed'] font-bold tracking-wider px-5 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? 'SAVING...' : 'SAVE DRAFT'}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => handleSubmit(true)}
            className="bg-[#00ff41] hover:bg-[#00e63a] text-black font-['Barlow_Condensed'] font-black text-sm tracking-widest px-6 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? 'PUBLISHING...' : 'PUBLISH NOW'}
          </button>
        </div>
      </div>
    </form>
  );
}
